import { useEffect, useRef } from 'react'
import { useAuth } from '@/features/auth/hooks/use-auth'
import { subscribeToPush, isPushSupported } from '@/shared/services/push-notifications'

const PUSH_ASKED_KEY = 'push-permission-asked'

/**
 * Invisible component that subscribes the user to push notifications
 * after login, if they haven't been asked yet.
 * Shows the native browser permission prompt once per device.
 */
export function PushNotificationInit() {
  const { isAuthenticated } = useAuth()
  const attempted = useRef(false)

  useEffect(() => {
    if (!isAuthenticated || attempted.current) return
    if (!isPushSupported()) return

    // Only ask once per device
    if (Notification.permission !== 'default') {
      // Already granted or denied — if granted, ensure subscription is active
      if (Notification.permission === 'granted') {
        attempted.current = true
        subscribeToPush().catch(() => {})
      }
      return
    }

    // Don't re-ask if we already prompted on this device
    const alreadyAsked = localStorage.getItem(PUSH_ASKED_KEY)
    if (alreadyAsked) return

    // Wait a bit after login before asking (less intrusive)
    const timer = setTimeout(() => {
      attempted.current = true
      localStorage.setItem(PUSH_ASKED_KEY, '1')
      subscribeToPush().catch(() => {})
    }, 5000)

    return () => clearTimeout(timer)
  }, [isAuthenticated])

  return null
}
